import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LOGIN, excludeFromLayoutRedirection } from "../routes/RouteConstants";
import UserService from "../services/UserService";
import { pageAfterLoading } from "../services/utils";
import { useUserProfile } from "../queries/userProfile/useUserProfileQueries";

/**
 * Custom hook to handle authentication loading, profile fetching and redirection
 */
interface AuthLoaderOptions {
  enabled?: boolean;
  redirectTimeout?: number;
}

const useAuthLoader = ({
  enabled = true,
  redirectTimeout = 5000
}: AuthLoaderOptions = {}): { isLoading: boolean } => {
  if (!enabled) {
    return { isLoading: false };
  }
  const router = useRouter();
  const dispatch = useDispatch();
  // Use a ref to track if authentication has already been performed
  const hasRunAuth = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  // Use our new custom hook for fetching profile
  const { isLoading: isProfileLoading, data: profileData } = useUserProfile({
    callback: res => {
      if (excludeFromLayoutRedirection.includes(router.pathname)) {
        return;
      }

      const targetUrl = pageAfterLoading(res);

      if (targetUrl) {
        try {
          // Extract path without query parameters and decode it
          const targetPath = decodeURIComponent(targetUrl.split("?")[0]);
          // Decode current path for consistent comparison
          const currentPath = decodeURIComponent(router.pathname);

          if (currentPath !== targetPath) {
            router.push(targetUrl);
          }
        } catch (e) {
          // Fallback in case of decoding errors
          const targetPath = targetUrl.split("?")[0];
          if (router.pathname !== targetPath) {
            router.push(targetUrl);
          }
        }
      }
    }
  });

  useEffect(() => {
    // Track loading state
    setIsLoading(isProfileLoading);
  }, [isProfileLoading]);

  useEffect(() => {
    // Only run the authentication logic once per component instance
    if (hasRunAuth.current) {
      return;
    }

    const handleAuthentication = async () => {
      // Mark that we've run the authentication logic
      hasRunAuth.current = true;

      const rec_access_token = UserService.getAccessToken();

      // If no token, set timeout to redirect to login
      if (!rec_access_token) {
        const timeoutId = setTimeout(() => {
          if (!UserService.isAuthenticated()) {
            router.push(LOGIN);
          }
        }, redirectTimeout);

        // Clean up timeout on unmount
        return () => clearTimeout(timeoutId);
      }
    };

    handleAuthentication();
  }, [router, dispatch, redirectTimeout]);

  return { isLoading };
};

export default useAuthLoader;
